import type { MessageReplyContextDto } from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import { ReplyQuoteStrip } from "./ReplyQuoteStrip.js";
import { replyQuoteFromContext } from "./reply-quote-model.js";

export interface MessageReplyPreviewProps {
  replyContext: MessageReplyContextDto;
  organizationId?: number;
  onJumpToQuotedMessage?: (messageId: string) => void;
  className?: string;
}

/**
 * Compact quoted/reply block for inbound Meta context.id resolution.
 * Framed as an inline quote inside the parent message bubble.
 */
export function MessageReplyPreview({
  replyContext,
  organizationId,
  onJumpToQuotedMessage,
  className,
}: MessageReplyPreviewProps): React.JSX.Element {
  const { t } = useTranslation();
  const presentation = replyQuoteFromContext(replyContext, t);
  const canJump =
    presentation != null &&
    replyContext.messageId != null &&
    onJumpToQuotedMessage != null;

  if (presentation == null) {
    return (
      <div
        className={cn(
          "mb-1 max-w-full rounded-md bg-black/[0.04] px-2 py-1",
          className,
        )}
        data-testid="message-reply-preview"
      >
        <p className="m-0 text-[0.75rem] font-medium leading-snug text-ink-muted">
          {t("inbox.replyTo.unavailable")}
        </p>
      </div>
    );
  }

  const strip = (
    <ReplyQuoteStrip
      presentation={presentation}
      organizationId={organizationId}
      className="bg-black/[0.04]"
    />
  );

  if (canJump) {
    return (
      <button
        type="button"
        className={cn(
          "mb-1 grid w-full max-w-full rounded-md text-start hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
          className,
        )}
        data-testid="message-reply-preview"
        onClick={() => onJumpToQuotedMessage(replyContext.messageId!)}
      >
        {strip}
      </button>
    );
  }

  return (
    <div className={cn("mb-1 max-w-full", className)} data-testid="message-reply-preview">
      {strip}
    </div>
  );
}
